import { formatNumberWithSuffix, formatVal } from "./api/sharedFunction";
import { DataPoint } from "./table-objects";

/**
 * Card view stays compact through this viewport width. Above it, each card is wide
 * enough for full-size OHLCV on one line (expand/modal is always full size).
 */
export const OHLCV_CARD_COMPACT_MAX_PX = 1708;

/** Fixed outer height for top-row dashboard chart cards so paired cards stay aligned. */
export const DASHBOARD_CHART_CARD_HEIGHT_CLASS = "h-[378px]";

/**
 * Fixed outer height for bottom-row dashboard cards (HPB, Volume Analysis, Research Insights).
 * All three share the same height per breakpoint so the row stays symmetric.
 * xl–1708px uses a shorter height so bars don't look oversized on ~1440–1538px screens.
 */
export const DASHBOARD_COMPACT_CHART_CARD_HEIGHT_CLASS =
  "h-[210px] lg:h-[220px] xl:h-[250px] min-[1709px]:h-[308px]";

/**
 * Shared echarts grid for HPB + Volume Analysis dashboard cards.
 * Matching top/bottom reserves the same plot height and x-axis baseline across the row.
 */
export const DASHBOARD_COMPACT_ECHARTS_GRID = {
  top: 32,
  left: 38,
  right: 6,
  bottom: 28,
  containLabel: false,
} as const;

/**
 * Card view: single line, no wrap — prevents header height jumps / chart flicker on hover.
 * Modal view: may wrap when space allows.
 */
export const candleDetailsCardWrapperClass =
  "flex flex-nowrap items-center gap-x-1 min-[1709px]:gap-x-2 overflow-hidden min-w-0 max-[1708px]:text-[13px] max-[1708px]:leading-none text-sm leading-normal font-semibold text-black dark:text-white tabular-nums whitespace-nowrap min-h-[14px] pr-8 lg:pr-10";

export const candleDetailsModalWrapperClass =
  "flex flex-wrap items-center gap-x-1.5 sm:gap-x-2 gap-y-1 text-[12px] xl:text-[13px] font-semibold text-black dark:text-white tabular-nums pr-6 xl:pr-8";

export const getCandleDetailsTitle = (
  candle: DataPoint | null | undefined,
): string | undefined => {
  if (!candle) return undefined;
  return `O: ${formatVal(candle.open)} H: ${formatVal(candle.high)} L: ${formatVal(candle.low)} C: ${formatVal(candle.close)} V: ${formatNumberWithSuffix(candle.volume ?? 0)}`;
};

/** Whether the OHLCV header row is hidden for a module at the given viewport width. */
export const isOhlcvHeaderHidden = (
  moduleName: string,
  windowWidth: number,
): boolean => {
  if (moduleName === "order-block") return windowWidth <= 1750;
  if (moduleName === "dashboard") return windowWidth <= 1100;
  return false;
};

export const renderCandleDetailsReact = (
  candle: DataPoint | null | undefined,
) => {
  if (!candle) return null;
  const isBullish = candle.close >= candle.open;
  const colorClass = isBullish
    ? "font-normal text-emerald-500"
    : "font-medium text-red-500";

  const itemClass =
    "inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap min-[1709px]:gap-1.5";

  return (
    <>
      <span className={itemClass}>
        <span>O</span>
        <span className={colorClass}>{formatVal(candle.open)}</span>
      </span>
      <span className={itemClass}>
        <span>H</span>
        <span className={colorClass}>{formatVal(candle.high)}</span>
      </span>
      <span className={itemClass}>
        <span>L</span>
        <span className={colorClass}>{formatVal(candle.low)}</span>
      </span>
      <span className={itemClass}>
        <span>C</span>
        <span className={colorClass}>{formatVal(candle.close)}</span>
      </span>
      <span className={itemClass}>
        <span>V</span>
        <span className={colorClass}>
          {formatNumberWithSuffix(candle.volume ?? 0)}
        </span>
      </span>
    </>
  );
};

export const renderCandleDetailsHtml = (
  candle: DataPoint | null | undefined,
  variant: "card" | "modal" = "modal",
) => {
  if (!candle) return "";
  const isBullish = candle.close >= candle.open;
  const colorClass = isBullish ? "#10b981" : "#ef4444";
  const wrapperClass =
    variant === "card"
      ? candleDetailsCardWrapperClass
      : candleDetailsModalWrapperClass;

  const itemStyle =
    variant === "card"
      ? "display:inline-flex; flex-shrink:0; align-items:center; gap:2px; white-space:nowrap;"
      : "display:flex; align-items:center; gap:3px;";

  return `
    <div class="${wrapperClass}"${variant === "card" ? ' style="flex-wrap:nowrap;"' : ""}>
      <span style="${itemStyle}"><span>O</span> <span style="color:${colorClass}; font-weight:500;">${formatVal(candle.open)}</span></span>
      <span style="${itemStyle}"><span>H</span> <span style="color:${colorClass}; font-weight:500;">${formatVal(candle.high)}</span></span>
      <span style="${itemStyle}"><span>L</span> <span style="color:${colorClass}; font-weight:500;">${formatVal(candle.low)}</span></span>
      <span style="${itemStyle}"><span>C</span> <span style="color:${colorClass}; font-weight:500;">${formatVal(candle.close)}</span></span>
      <span style="${itemStyle}"><span>V</span> <span style="color:${colorClass}; font-weight:500;">${formatNumberWithSuffix(candle.volume ?? 0)}</span></span>
    </div> 
  `;
};
